import { useEffect, useState } from 'react';

import { AdminPermissions } from 'constants/admin-permissions';
import { useAccessControl } from 'hooks/access-control';
import { useTracking } from 'hooks/analytic-tracking';
import { useDocumentTitle } from 'hooks/document-title';
import AdminPage from 'layouts/AdminPage/AdminPage';
import { Tab, Tabs } from 'react-bootstrap';
import Moment from 'react-moment';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { NewCompanyModal } from './components/NewCompanyModal/NewCompanyModal';
import { Table } from 'components/Table/Table';

import { selectAuthentication } from 'api/authentication-slice';
import { useCreateCompanyOnBehalfMutation, useLazyGetCompaniesQuery } from 'api/company-api';

import AdminCompanyGroups from '../AdminCompanyGroups/AdminCompanyGroups';

const AdminCompanies = ({}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'groups' ? 'groups' : 'companies';

  useDocumentTitle(tab === 'groups' ? 'Company Groups' : 'Companies');

  const navigate = useNavigate();
  const { trackEvent } = useTracking();

  const [loading, setLoading] = useState(true);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [createGroupSignal, setCreateGroupSignal] = useState(0);
  const [companies, setCompanies] = useState([]);
  const authentication = useSelector(selectAuthentication);

  const [showNewCompanyModal, setShowNewCompanyModal] = useState(false);

  const [getAllCompanies] = useLazyGetCompaniesQuery();
  const [createCompany] = useCreateCompanyOnBehalfMutation();

  const hasPermission = useAccessControl(authentication.admin_permissions);

  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });

  const fetchCompanies = async (params = {}) => {
    setLoading(true);
    const { result } = await getAllCompanies(params).unwrap();
    setPagination(result.pagination);
    setCompanies(result.companies);
    setLoading(false);
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleCompanyCreation = async data => {
    setLoading(true);
    const { data: companyId } = await createCompany(data);

    trackEvent('Company Created', {
      companyName: data.company.name,
      companyCountry: data.billing_entity.country,
      source: '/admin/companies',
      userFirstName: data.user.first_name,
      userLastName: data.user.last_name,
      userEmail: data.user.email,
      groupId: companyId,
    });

    setLoading(false);
    navigate(`/admin/companies/${companyId}`);
  };

  const selectTab = nextTab => {
    if (!nextTab || nextTab === tab) return;
    if (nextTab === 'groups') setSearchParams({ tab: 'groups' });
    else setSearchParams({});
  };

  return (
    <AdminPage
      loading={loading || (tab === 'groups' && groupsLoading)}
      title="Companies"
      subtitle={tab === 'groups' ? 'Group companies that share an invoice addressee' : undefined}
      button={
        tab === 'groups'
          ? hasPermission(AdminPermissions.UpdateCompany)
          : hasPermission(AdminPermissions.CreateCompany)
      }
      buttonText={tab === 'groups' ? 'Create company group' : 'Create Company'}
      onClick={() => {
        if (tab === 'groups') setCreateGroupSignal(value => value + 1);
        else setShowNewCompanyModal(true);
      }}
      allowedRole={AdminPermissions.ListCompanies}>
      {hasPermission(AdminPermissions.CreateCompany) && (
        <NewCompanyModal
          visible={showNewCompanyModal}
          onSubmit={handleCompanyCreation}
          onClose={() => setShowNewCompanyModal(false)}
        />
      )}
      <Tabs
        activeKey={tab}
        onSelect={selectTab}
        className="tabs no-padding padding-small"
        mountOnEnter>
        <Tab eventKey="companies" title="Companies" className="u-padding--large">
          <Table.Context onQueryUpdate={fetchCompanies} defaultQuery={{ page: 1 }}>
            <Table.TableContent>
              <Table.Filters>
                <Table.LeftFilters>
                  <Table.Search placeholder="Search for a company..." />
                </Table.LeftFilters>
                <Table.RightFilters>
                  <Table.Filter
                    title="Status"
                    filterName="active"
                    options={[
                      { value: '1', name: 'Active' },
                      { value: '0', name: 'Inactive' },
                    ]}
                  />
                </Table.RightFilters>
              </Table.Filters>
              <Table className="u-margin--large">
                <Table.Head>
                  <Table.Row>
                    <Table.Header>ID</Table.Header>
                    <Table.Header sortKey="companies.name">Name</Table.Header>
                    <Table.Header>Owner Email</Table.Header>
                    <Table.Header>Country</Table.Header>
                    <Table.Header sortKey="companies.created_at">Created at</Table.Header>
                    <Table.Header>User Type</Table.Header>
                    <Table.Header>Account Manager</Table.Header>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {companies.map(company => (
                    <Table.Row key={company.id} style={{ opacity: company.active ? 1 : 0.5 }}>
                      <Table.Col>
                        <Link to={`/admin/companies/${company.id}`} className="u-link dark">
                          {company.id}
                        </Link>
                      </Table.Col>
                      <Table.Col>{company.name}</Table.Col>
                      <Table.Col>{company.owner_email}</Table.Col>
                      <Table.Col>{company.country}</Table.Col>
                      <Table.Col>
                        <Moment format="DD/MM/YYYY">{company.created_at}</Moment>
                      </Table.Col>
                      <Table.Col className="u-capitalize">{company.account_type}</Table.Col>
                      <Table.Col className="u-capitalize">{company.account_manager}</Table.Col>
                    </Table.Row>
                  ))}
                </Table.Body>
                <Table.Pagination {...pagination} />
              </Table>
            </Table.TableContent>
          </Table.Context>
        </Tab>
        <Tab eventKey="groups" title="Company Groups" className="u-padding--large">
          <AdminCompanyGroups createSignal={createGroupSignal} onLoadingChange={setGroupsLoading} />
        </Tab>
      </Tabs>
    </AdminPage>
  );
};

export default AdminCompanies;
